import { useEffect, useMemo, useState } from 'react'
import {
  addTickListener,
  subscribe,
  subscribeBook,
  unsubscribe,
} from '../api/liveQuotes'
import type { LiveQuoteTick, StreamBookMessage } from '../api/liveQuotes'
import { useSetting } from '../hooks/useSetting'
import { usePanelFrame } from '../hooks/usePanelFrame'

const PANEL_MIN_KEY = 'ticker-l2-panel-minimized'
const MAX_ROWS = 16

type Venue = 'NASDAQ' | 'NYSE'
const VENUES: Venue[] = ['NASDAQ', 'NYSE']
const VENUE_SHORT: Record<Venue, string> = { NASDAQ: 'NSDQ', NYSE: 'NYSE' }

interface MontageRow {
  mpid: string
  price: number
  size: number
}

// Classic montage palette — each distinct price level gets the next band
// color so depth tiers line up visually across both sides.
const BAND_CLASSES = [
  'bg-green-400',
  'bg-red-400',
  'bg-yellow-300',
  'bg-cyan-300',
  'bg-blue-400',
]

/**
 * Level-2 montage, streamed from the Schwab book services. Desktop only —
 * docks to the left of the floating trade panel. NASDAQ and NYSE book
 * snapshots are merged and flattened to per-MPID rows, sorted best-first,
 * with price-level color banding (DAS-style).
 */
export function Level2Panel({ symbol }: { symbol: string }) {
  const [minimized, setMinimized] = useSetting<boolean>(PANEL_MIN_KEY, false)
  const frame = usePanelFrame('ticker-l2-frame', { minHeight: 220 })
  const [books, setBooks] = useState<Partial<Record<Venue, StreamBookMessage>>>({})
  const [tick, setTick] = useState<LiveQuoteTick | null>(null)

  useEffect(() => {
    setBooks({})
    setTick(null)
    // The book stream is chatty — only hold the upstream subscription while
    // the montage is actually visible.
    if (!symbol || minimized) return
    const offBook = subscribeBook(symbol, (msg) => {
      setBooks((prev) => ({ ...prev, [msg.venue]: msg }))
    })
    subscribe([symbol])
    const offTick = addTickListener(symbol, (t) => {
      setTick(t)
    })
    return () => {
      offBook()
      offTick()
      unsubscribe([symbol])
    }
  }, [symbol, minimized])

  const bids = useMemo(() => flattenSide(books, 'bids'), [books])
  const asks = useMemo(() => flattenSide(books, 'asks'), [books])
  const bidBands = useMemo(() => bandBySide(bids), [bids])
  const askBands = useMemo(() => bandBySide(asks), [asks])
  const rowCount = Math.max(bids.length, asks.length)

  return (
    <div
      ref={frame.ref}
      style={frame.style(!minimized)}
      className={`hidden lg:flex lg:flex-col fixed bottom-0 right-[calc(5rem+540px+12px)] w-[480px] z-30 border border-zinc-800 bg-zinc-950/95 shadow-2xl backdrop-blur-md ${
        frame.detached ? 'rounded-lg' : 'rounded-t-lg border-b-0'
      } ${minimized ? 'max-h-[36px] overflow-hidden' : ''}`}
    >
      <div
        onPointerDown={frame.onHeaderPointerDown}
        onClick={() => {
          if (frame.consumeDragClick()) return
          setMinimized(!minimized)
        }}
        className="flex items-center justify-between gap-2 px-3 h-[36px] shrink-0 border-b border-zinc-800 bg-white/[0.02] rounded-t-lg cursor-pointer select-none"
        title={frame.detached ? undefined : 'Drag to detach'}
      >
        <span className="flex-1 min-w-0 text-xs uppercase tracking-wide text-zinc-400 font-medium truncate">
          Depth · <span className="text-zinc-100 font-semibold">{symbol}</span>
        </span>
        {frame.detached && (
          <button
            type="button"
            onClick={frame.dock}
            aria-label="Dock panel to bottom"
            title="Dock to bottom"
            className="p-1 rounded text-zinc-400 hover:text-zinc-100 hover:bg-white/5 transition"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <path d="M12 3v11" />
              <polyline points="7 9 12 14 17 9" />
              <line x1="5" y1="20" x2="19" y2="20" />
            </svg>
          </button>
        )}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            setMinimized(!minimized)
          }}
          aria-label={minimized ? 'Expand depth panel' : 'Minimize depth panel'}
          title={minimized ? 'Expand' : 'Minimize'}
          className="p-1 rounded text-zinc-400 hover:text-zinc-100 hover:bg-white/5 transition"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={`transition-transform ${minimized ? 'rotate-180' : ''}`}
            aria-hidden
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
      </div>
      {!minimized && (
        <>
          <div className="flex items-center justify-between px-2 py-1 shrink-0 text-[11px] tabular-nums border-b border-zinc-800 bg-white/[0.02]">
            <span className="text-zinc-500">
              CHG %{' '}
              <span
                className={
                  (tick?.netChange ?? 0) >= 0 ? 'text-emerald-400' : 'text-red-400'
                }
              >
                {formatPct(tick?.netPercentChange)}
              </span>
            </span>
            <span className="text-zinc-500">
              VOL <span className="text-zinc-200">{formatVolume(tick?.totalVolume)}</span>
            </span>
          </div>
          <div
            className={`flex-1 min-h-0 overflow-hidden ${
              frame.height != null ? '' : 'max-h-[330px]'
            }`}
          >
            {rowCount === 0 ? (
              <div className="px-3 py-6 text-xs text-zinc-500">
                Waiting for depth data… Level 2 streams during market hours.
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-px bg-zinc-800 text-[11px] leading-[18px] tabular-nums font-medium content-start h-full">
                <div className="flex flex-col bg-zinc-950">
                  {bids.map((row, i) => (
                    <MontageRowView key={i} row={row} band={bidBands[i]} />
                  ))}
                </div>
                <div className="flex flex-col bg-zinc-950">
                  {asks.map((row, i) => (
                    <MontageRowView key={i} row={row} band={askBands[i]} />
                  ))}
                </div>
              </div>
            )}
          </div>
          {frame.detached && (
            <div
              onPointerDown={frame.onResizePointerDown}
              title="Drag to resize"
              className="h-2 shrink-0 cursor-ns-resize rounded-b-lg hover:bg-white/10 transition"
            />
          )}
        </>
      )}
    </div>
  )
}

function MontageRowView({ row, band }: { row: MontageRow; band: number }) {
  return (
    <div
      className={`grid grid-cols-[1fr_auto_auto] gap-1.5 px-1.5 text-black ${
        BAND_CLASSES[band % BAND_CLASSES.length]
      }`}
    >
      <span className="truncate font-semibold">{row.mpid}</span>
      <span className="text-right">{row.price.toFixed(2)}</span>
      <span className="text-right min-w-[3ch]">{formatSize(row.size)}</span>
    </div>
  )
}

// Merge both venue books into per-MPID rows, best price first. Levels that
// arrive without a per-MM breakdown fall back to one aggregate row labeled
// with the venue.
function flattenSide(
  books: Partial<Record<Venue, StreamBookMessage>>,
  side: 'bids' | 'asks',
): MontageRow[] {
  const rows: MontageRow[] = []
  for (const venue of VENUES) {
    const book = books[venue]
    if (!book) continue
    for (const lvl of book[side]) {
      if (lvl.mms.length > 0) {
        for (const mm of lvl.mms) {
          rows.push({ mpid: mm.id || VENUE_SHORT[venue], price: lvl.price, size: mm.size })
        }
      } else {
        rows.push({ mpid: VENUE_SHORT[venue], price: lvl.price, size: lvl.size })
      }
    }
  }
  rows.sort(
    side === 'bids'
      ? (a, b) => b.price - a.price || b.size - a.size
      : (a, b) => a.price - b.price || b.size - a.size,
  )
  return rows.slice(0, MAX_ROWS)
}

// Band index per row: increments on each distinct price, so equal-priced
// rows share a color and the palette cycles down the ladder.
function bandBySide(rows: MontageRow[]): number[] {
  let band = -1
  let lastPrice = NaN
  return rows.map((r) => {
    if (r.price !== lastPrice) {
      band += 1
      lastPrice = r.price
    }
    return band
  })
}

// Montages show size in round lots; if the upstream already sends small
// numbers, pass them through untouched.
function formatSize(size: number): string {
  if (size >= 100) return Math.round(size / 100).toLocaleString()
  return String(size)
}

function formatPct(pct: number | undefined): string {
  if (pct == null || !Number.isFinite(pct)) return '—'
  return `${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%`
}

function formatVolume(vol: number | undefined): string {
  if (vol == null || !Number.isFinite(vol)) return '—'
  if (vol >= 1_000_000) return `${(vol / 1_000_000).toFixed(2)}M`
  if (vol >= 10_000) return `${(vol / 1_000).toFixed(1)}K`
  return vol.toLocaleString()
}
